import { useCallback, useMemo, useState } from "react";
import { ExerciseData, ExerciseNavigationHookReturn } from "../types";

export const useExerciseNavigation = (): ExerciseNavigationHookReturn => {
  const [{ exerciseHistory, currentHistoryIndex }, setHistoryState] = useState<{
    exerciseHistory: ExerciseData[];
    currentHistoryIndex: number;
  }>({ exerciseHistory: [], currentHistoryIndex: -1 });
  const [isNavigating, setIsNavigating] = useState(false);

  const canGoToPrevious = currentHistoryIndex > 0;
  const canGoToNext = currentHistoryIndex < exerciseHistory.length - 1;

  const goToPreviousExercise = useCallback(() => {
    if (canGoToPrevious) {
      setIsNavigating(true);
      setHistoryState((prev) => ({ ...prev, currentHistoryIndex: prev.currentHistoryIndex - 1 }));
      setTimeout(() => setIsNavigating(false), 100);
    }
  }, [canGoToPrevious]);

  const goToNextExercise = useCallback(
    (onGenerateNew: () => void) => {
      if (canGoToNext) {
        // Navigate to next exercise in history
        setIsNavigating(true);
        setHistoryState((prev) => ({ ...prev, currentHistoryIndex: prev.currentHistoryIndex + 1 }));
        setTimeout(() => setIsNavigating(false), 100);
      } else {
        // Generate new exercise
        onGenerateNew();
      }
    },
    [canGoToNext]
  );

  const addToHistory = useCallback((exercise: ExerciseData) => {
    setHistoryState((prev) => {
      // Keep history to reasonable size (max 50 exercises).
      const newHistory = [...prev.exerciseHistory, exercise].slice(-50);
      return { exerciseHistory: newHistory, currentHistoryIndex: newHistory.length - 1 };
    });
    // A newly generated exercise owns the selection, even if history navigation
    // was still finishing when its response arrived.
    setIsNavigating(false);
  }, []);

  const resetNavigation = useCallback(() => {
    setHistoryState({ exerciseHistory: [], currentHistoryIndex: -1 });
    setIsNavigating(false);
  }, []);

  return useMemo(
    () => ({
      exerciseHistory,
      currentHistoryIndex,
      isNavigating,
      canGoToPrevious,
      canGoToNext,
      goToPreviousExercise,
      goToNextExercise,
      addToHistory,
      resetNavigation,
    }),
    [
      exerciseHistory,
      currentHistoryIndex,
      isNavigating,
      canGoToPrevious,
      canGoToNext,
      goToPreviousExercise,
      goToNextExercise,
      addToHistory,
      resetNavigation,
    ]
  );
};
